// Esquema de Atención Ciudadana — vista admin del municipio. Muestra, con colores,
// cómo se atiende cada reporte: categoría → dependencia → director, el ciclo de vida
// del ticket y las reglas de zona/tiempos. Las credenciales de cada director (para
// entregar acceso) se leen EN VIVO del backend y se enriquecen sobre el esquema.
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { getBuzonMatrix, getBuzonEsquema, approveBuzonMatrix } from './api';
import type { EsquemaBuzon, BuzonMatrix } from './types';
import { CheckCircle2, Grid3x3, ListTree, RefreshCw, Table2 } from 'lucide-react';
import { OsApp, OsTabs, osActionBtn } from '@/modules/orion-shell/OsApp';

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('es-MX', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

// Credencial (usuario / contraseña): monospace + copiar al portapapeles.
function Cred({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  if (!value) return <span className="text-muted-foreground">—</span>;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch { /* clipboard bloqueado: el valor igual se ve */ }
  };
  return (
    <button type="button" onClick={copy} title="Copiar"
      className="font-mono text-foreground hover:text-primary transition-colors inline-flex items-center gap-1">
      {value}
      <span className="text-[10px] text-muted-foreground">{copied ? '✓' : '⧉'}</span>
    </button>
  );
}

// Íconos por dependencia (stroke = currentColor → toma el hue).
function DepIcon({ k }: { k: string }) {
  const P = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  const paths: Record<string, ReactNode> = {
    obras: <><path {...P} d="M14 6l-8 8-3 6 6-3 8-8" /><path {...P} d="M18 2l4 4-4 4-4-4z" /></>,
    agua: <path {...P} d="M12 2s6 7 6 11a6 6 0 0 1-12 0c0-4 6-11 6-11Z" />,
    serv: <path {...P} d="M9 18h6M10 21h4M12 3a6 6 0 0 0-3 11c.6.4 1 1 1 1.8V16h4v-.2c0-.8.4-1.4 1-1.8A6 6 0 0 0 12 3Z" />,
    eco: <><path {...P} d="M11 20A7 7 0 0 1 9.8 6.1C15 5 17 3 19 4c1 2-1 4-2.1 9A7 7 0 0 1 11 20Z" /><path {...P} d="M9 15c2-3 5-4 8-4.5" /></>,
    seg: <path {...P} d="M12 3 5 6v5c0 4 3 7 7 9 4-2 7-5 7-9V6l-7-3Z" />,
    centro: <path {...P} d="M3 21h18M5 21V9l7-5 7 5v12M9 21v-6h6v6" />,
    otro: <><path {...P} d="M12 21a9 9 0 1 0-9-9" /><path {...P} d="M9 10a3 3 0 1 1 4 2.8c-.8.4-1 .9-1 1.7M12 18h.01" /></>,
  };
  return <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">{paths[k]}</svg>;
}

// 🎨 SÓLO PRESENTACIÓN. El esquema (dependencias, buzones, directores y categorías) llega del
// BACKEND vía /buzon-esquema; aquí sólo se elige color e ícono, con default para dependencias
// nuevas. Antes esto era un SCHEME escrito a mano y por eso el tablero seguía mostrando
// categorías y buzones que el municipio ya había retirado.
const HUE: Record<string, string> = {
  'Obras Públicas': '#4a7ba6',
  'Interapas': '#2593b8',
  'Servicios Municipales': '#c08a1e',
  'Ecología': '#57a046',
  'Seguridad': '#b8564a',
  'Unidad de Gestión Centro Histórico': '#8a68b8',
  'Desarrollo Urbano': '#7a6a55',
  'Comercio': '#3f8f7a',
};
const ICONO: Record<string, string> = {
  'Obras Públicas': 'obras',
  'Interapas': 'agua',
  'Servicios Municipales': 'serv',
  'Ecología': 'eco',
  'Seguridad': 'seg',
  'Unidad de Gestión Centro Histórico': 'centro',
};
const hueDe = (dep: string) => HUE[dep] ?? '#78867f';
const iconoDe = (dep: string): string => ICONO[dep] ?? 'otro';

const LIFECYCLE = [
  { n: '1', v: 'Recibido', c: '#a5641a', d: 'Entra por un canal del agente (WhatsApp, web…), se clasifica y se genera el folio.' },
  { n: '2', v: 'Asignado', c: '#2593b8', d: 'Turnado al buzón de la dependencia responsable.' },
  { n: '3', v: 'En proceso', c: '#2593b8', d: 'El director lo toma y trabaja la solución.' },
  { n: '4', v: 'Atendido', c: '#57a046', d: 'Resuelto; se adjunta evidencia del arreglo.' },
  { n: '5', v: 'Cerrado', c: '#78867f', d: 'Concluido y notificado al ciudadano.' },
];

export function BuzonMatrixPage() {
  const [data, setData] = useState<BuzonMatrix | null>(null);
  const [esquema, setEsquema] = useState<EsquemaBuzon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);
  const [note, setNote] = useState('');
  const [toast, setToast] = useState<string | null>(null);
  const [esquemaAviso, setEsquemaAviso] = useState<string | null>(null);
  const [vista, setVista] = useState<'esquema' | 'matriz' | 'aprobar'>('esquema');

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      // El esquema es opcional (backends sin /buzon-esquema): si falla, la vista se arma
      // con la matriz (/buzon-matrix) y se avisa — nunca se esconde el error.
      const [m, e] = await Promise.all([
        getBuzonMatrix(),
        getBuzonEsquema().then(
          (r) => { setEsquemaAviso(null); return Array.isArray(r) ? r : []; },
          (err: any) => { setEsquemaAviso(err?.message || 'No se pudo leer el esquema de categorías.'); return [] as EsquemaBuzon[]; },
        ),
      ]);
      setData(m); setEsquema(e);
    }
    catch (e: any) { setError(e?.message || 'No se pudo cargar la matriz.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Vista: el esquema vivo; si el backend no lo da, las dependencias de la matriz
  // (mismos responsables, sin desglose por categoría).
  const esquemaVista = useMemo<EsquemaBuzon[]>(() => {
    if (esquema.length || !data) return esquema;
    return (data.dependencies || []).map((d) => ({
      dependencia: d.dependency, buzon: d.dependency, buzones: [d.dependency],
      username: d.username, responsable: d.responsable, email: d.email, cargo: d.cargo,
      password: d.password, categorias: [],
    }));
  }, [esquema, data]);

  // Credenciales por director (username → {email, password}). El backend las da por
  // buzón; el mismo director puede tener varios buzones (mismas credenciales).
  const credByUser = useMemo(() => {
    const m: Record<string, { email: string; password: string }> = {};
    if (data) for (const d of data.dependencies) m[d.username] = { email: d.email, password: d.password };
    return m;
  }, [data]);

  // Resumen calculado del esquema VIVO (nada escrito a mano: cambia si el backend cambia).
  const resumen = useMemo<[string, string][]>(() => {
    const cats = new Set<string>(); const deps = new Set<string>(); const dirs = new Set<string>();
    for (const b of esquemaVista) {
      if (b.dependencia) deps.add(b.dependencia);
      if (b.username) dirs.add(b.username);
      for (const c of b.categorias || []) cats.add(c.slug);
    }
    return [[String(cats.size), 'Categorías'], [String(deps.size), 'Dependencias'], [String(dirs.size), 'Responsables con buzón'], [String(LIFECYCLE.length), 'Fases del ticket']];
  }, [esquemaVista]);
  // Reglas transversales: se muestran solo si el esquema de la org las contiene.
  const tieneZonaCentro = esquemaVista.some((b) => /centro hist[oó]rico/i.test(b.dependencia));
  const tieneBaches = esquemaVista.some((b) => (b.categorias || []).some((c) => c.slug === 'bache'));

  const onApprove = async () => {
    if (approving) return;
    setApproving(true); setError(null);
    try {
      await approveBuzonMatrix(note.trim());
      setNote(''); setToast('Esquema aprobado — traza registrada.');
      await load(); setTimeout(() => setToast(null), 4000);
    } catch (e: any) { setError(e?.message || 'No se pudo aprobar.'); }
    finally { setApproving(false); }
  };

  return (
    <OsApp
      icon={Grid3x3}
      title="Buzones"
      subtitle="Cómo se atiende cada reporte: categoría → dependencia → responsable"
      tabs={
        <OsTabs
          label="Vistas de Buzones"
          value={vista}
          onChange={setVista}
          items={[
            { key: 'esquema', label: 'Esquema', icon: ListTree },
            { key: 'matriz', label: 'Matriz', icon: Table2 },
            { key: 'aprobar', label: data?.approval ? 'Aprobado' : 'Aprobar', icon: CheckCircle2 },
          ]}
        />
      }
      actions={
        <button type="button" onClick={load} disabled={loading} className={osActionBtn}>
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden /> {loading ? 'Cargando…' : 'Actualizar'}
        </button>
      }
    >
    <div className="space-y-6">
      {toast && <div className="rounded-md border border-green-500/40 bg-green-500/10 px-4 py-2 text-sm text-green-600 dark:text-green-400">{toast}</div>}
      {esquemaAviso && <div role="status" className="rounded-2xl border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-sm text-amber-800 dark:text-amber-200">Sin desglose por categoría: {esquemaAviso}</div>}
      {error && <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-2 text-sm text-destructive">{error}</div>}

      {vista === 'esquema' && (<>
      {/* Stats */}
      <section className="grid gap-3 grid-cols-2 sm:grid-cols-4">
        {resumen.map(([n, l]) => (
          <div key={l} className="rounded-2xl border border-border bg-card p-4">
            <div className="text-2xl font-semibold text-primary">{n}</div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground mt-1 font-medium">{l}</div>
          </div>
        ))}
      </section>

      {/* Ciclo de vida */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-foreground">Ciclo de vida del reporte</h2>
        <div className="flex flex-wrap gap-2 rounded-2xl border border-border bg-card p-4">
          {LIFECYCLE.map((s) => (
            <div key={s.n} className="relative flex-1 min-w-[150px] rounded-lg bg-secondary p-3 space-y-1">
              <span className="absolute top-3 right-3 h-2.5 w-2.5 rounded-full" style={{ background: s.c }} />
              <div className="text-[0.65rem] font-bold uppercase tracking-wider text-muted-foreground">Fase {s.n}</div>
              <div className="font-semibold text-foreground">{s.v}</div>
              <div className="text-xs text-muted-foreground leading-snug">{s.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Dependencias */}
      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Dependencias y responsables</h2>
          <p className="text-sm text-muted-foreground">Cada dependencia agrupa sus categorías y las atiende un director con buzón y acceso propios.</p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {esquemaVista.map((d) => {
            const cred = credByUser[d.username];
            const hue = hueDe(d.dependencia);
            return (
              <article key={d.dependencia} className="rounded-2xl border border-border bg-card overflow-hidden flex flex-col">
                <div className="h-1.5" style={{ background: hue }} />
                <div className="p-4 flex flex-col gap-3 flex-1">
                  <div className="flex items-start gap-3">
                    <span className="h-9 w-9 rounded-lg grid place-items-center flex-none"
                      style={{ background: hue + '22', color: hue }}><DepIcon k={iconoDe(d.dependencia)} /></span>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-foreground leading-tight">{d.dependencia}</h3>
                      <div className="text-xs text-muted-foreground mt-0.5">Buzón · <span className="font-medium" style={{ color: hue }}>{(d.buzones && d.buzones.length ? d.buzones : [d.buzon]).join(' · ')}</span></div>
                    </div>
                  </div>

                  <div className="rounded-lg bg-secondary p-3 space-y-1.5">
                    <div className="text-[0.62rem] font-bold uppercase tracking-wider text-muted-foreground">Director responsable</div>
                    <div className="font-medium text-sm text-foreground">{d.responsable}</div>
                    <div className="text-xs text-muted-foreground break-all">{d.email || cred?.email || ''}</div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1 text-xs">
                      <span className="text-muted-foreground">Usuario: <Cred value={d.username} /></span>
                      <span className="text-muted-foreground">Contraseña: <Cred value={d.password || cred?.password || ''} /></span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-1.5 mt-auto">
                    {(d.categorias || []).map((c) => (
                      <span key={`${d.dependencia}:${c.slug}`} className="text-xs font-medium px-2.5 py-1 rounded-full text-foreground"
                        style={{ background: hue + '1c', border: `1px solid ${hue}44` }}>{c.label}</span>
                    ))}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      </>)}

      {vista === 'matriz' && (<>
      {/* Matriz completa */}
      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Matriz completa</h2>
          <p className="text-sm text-muted-foreground">Cada categoría del catálogo, su dependencia y el director que la recibe.</p>
        </div>
        {/* Móvil: lista (sin tabla ancha) */}
        <ul className="divide-y divide-border rounded-2xl border border-border bg-card sm:hidden">
          {esquemaVista.flatMap((d) => (d.categorias || []).map((c) => (
            <li key={`m:${d.dependencia}:${c.slug}`} className="px-4 py-3">
              <div className="font-medium text-foreground">{c.label}</div>
              <div className="mt-0.5 flex items-center gap-2 text-sm text-muted-foreground">
                <span className="h-2 w-2 flex-none rounded-full" style={{ background: hueDe(d.dependencia) }} aria-hidden />
                <span className="min-w-0 truncate">{d.dependencia} · {d.responsable || 'sin responsable'}</span>
              </div>
            </li>
          )))}
          {!esquemaVista.some((d) => (d.categorias || []).length) && (
            <li className="px-4 py-8 text-center text-sm text-muted-foreground">El servidor aún no expone el desglose por categoría.</li>
          )}
        </ul>
        <div className="hidden rounded-2xl border border-border bg-card overflow-hidden sm:block">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-border">
                  <th className="px-4 py-3 font-medium">Categoría</th>
                  <th className="px-4 py-3 font-medium">Dependencia</th>
                  <th className="px-4 py-3 font-medium">Director responsable</th>
                </tr>
              </thead>
              <tbody>
                {esquemaVista.flatMap((d) => (d.categorias || []).map((c) => (
                  <tr key={`${d.dependencia}:${c.slug}`} className="border-b border-border/50 last:border-0 hover:bg-accent/40">
                    <td className="px-4 py-2.5 text-foreground font-medium">{c.label}</td>
                    <td className="px-4 py-2.5">
                      <span className="inline-flex items-center gap-2 font-medium text-foreground">
                        <span className="h-2 w-2 rounded-full flex-none" style={{ background: hueDe(d.dependencia) }} />{d.dependencia}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap">{d.responsable}</td>
                  </tr>
                )))}
                {!esquemaVista.some((d) => (d.categorias || []).length) && (
                  <tr><td colSpan={3} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    El servidor aún no expone el desglose por categoría. Las dependencias y sus responsables están en «Esquema».
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      </>)}

      {vista === 'esquema' && (<>
      {/* Reglas transversales */}
      {(tieneZonaCentro || tieneBaches) && (
      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-foreground">Reglas que aplican a todos</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          {tieneZonaCentro && (
          <div className="rounded-2xl border border-border bg-card p-4 space-y-1.5">
            <div className="flex items-center gap-2 font-semibold text-foreground">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#8a68b8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21s-7-4.4-7-10a7 7 0 0 1 14 0c0 5.6-7 10-7 10Z" /><circle cx="12" cy="11" r="2.4" /></svg>
              Zona Centro Histórico
            </div>
            <p className="text-sm text-muted-foreground">Cualquier reporte cuya ubicación caiga dentro del polígono del Centro se <strong className="text-foreground">co-notifica a la Unidad de Gestión Centro Histórico</strong>, además de su dependencia responsable. Es visibilidad de zona, no cambia quién atiende.</p>
          </div>
          )}
          {tieneBaches && (
          <div className="rounded-2xl border border-border bg-card p-4 space-y-1.5">
            <div className="flex items-center gap-2 font-semibold text-foreground">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#a5641a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
              Tiempos de atención
            </div>
            <p className="text-sm text-muted-foreground">En baches se informa al ciudadano el tiempo aproximado: <strong className="text-foreground">~24 h en asfalto</strong> y <strong className="text-foreground">hasta 72 h en concreto</strong>, según el material y la carga de la cuadrilla.</p>
          </div>
          )}
        </div>
      </section>
      )}

      </>)}

      {vista === 'aprobar' && (<>
      {/* Aprobar */}
      <section className="rounded-2xl border border-border bg-card p-4 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-sm font-semibold text-foreground">Aprobar esquema</h2>
          {data && (data.approval
            ? <span className="text-xs font-medium text-green-600 dark:text-green-400">✓ Aprobado · {data.approval.approved_by} · {fmtDate(data.approval.approved_at)}</span>
            : <span className="text-xs font-medium text-amber-600 dark:text-amber-400">Sin aprobar</span>)}
        </div>
        <p className="text-xs text-muted-foreground">Al aprobar dejas constancia (quién y cuándo) de que este esquema es correcto. No modifica los buzones.</p>
        <div className="flex gap-2 flex-wrap">
          <input type="text" value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Nota (opcional): p. ej. revisado con presidencia"
            className="flex-1 min-w-[240px] rounded-md border border-border bg-background px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
          <button onClick={onApprove} disabled={approving}
            className="px-4 py-1.5 text-sm rounded-md bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 transition-opacity font-medium">
            {approving ? 'Aprobando…' : 'Aprobar esquema'}
          </button>
        </div>
      </section>
      </>)}
    </div>
    </OsApp>
  );
}
